import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authAPI, rbacAPI } from '@/lib/api';
import { tokenStore } from '@/lib/tokenStore';

const AuthContext = createContext(null);

const DEFAULT_ROLES = ['admin', 'manager', 'agent'];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userPermissions, setUserPermissions] = useState(null);
  const [isAdminPermissions, setIsAdminPermissions] = useState(false);

  const fetchPermissions = useCallback(async () => {
    try {
      const res = await authAPI.myPermissions();
      setUserPermissions(res.data.permissions || {});
      setIsAdminPermissions(!!res.data.is_admin);
    } catch {
      setUserPermissions(null);
      setIsAdminPermissions(false);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // If we have an in-memory token, use it
    const token = tokenStore.getToken();
    const savedUser = tokenStore.getUser();
    if (token && savedUser) {
      setUser(savedUser);
    }

    // Always try /api/auth/me — httpOnly cookie may restore the session
    try {
      const [meRes] = await Promise.all([authAPI.me(), fetchPermissions()]);
      const userData = meRes.data;
      setUser(userData);
      tokenStore.setUser(userData);
    } catch (err) {
      if (err?.response?.status === 401) {
        tokenStore.clear();
        setUser(null);
        setUserPermissions(null);
      }
    }
    setLoading(false);
  }, [fetchPermissions]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback((token, userData) => {
    tokenStore.setToken(token);
    tokenStore.setUser(userData);
    setUser(userData);
    // Routen warten, bis die Rechte da sind — sonst prueft ProtectedRoute die Zielseite (z. B. /trainer
    // fuers Trainer-Tablet) ohne Rechte und leitet auf die erste erlaubte Seite um
    setLoading(true);
    fetchPermissions().finally(() => setLoading(false));
  }, [fetchPermissions]);

  const logout = useCallback(async () => {
    try { await authAPI.logout(); } catch {}
    tokenStore.clear();
    setUser(null);
    setUserPermissions(null);
    setIsAdminPermissions(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, userPermissions, isAdminPermissions }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
